import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useToast } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ActivityFeedHookDocument,
  AdminDeletePostDocument,
  AdminUserPostsDocument,
  PostPagePostDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation AdminDeletePost($postId: UUID!) {
    adminDeletePost(input: { postId: $postId }) {
      __typename
    }
  }
`);

RefetchOnComplete({
  trigger: [AdminDeletePostDocument],
  refetch: [ActivityFeedHookDocument, PostPagePostDocument, AdminUserPostsDocument],
});

export function useAdminDeletePost({
  postId,
  onDeleteSuccess,
}: {
  postId: string;
  onDeleteSuccess: () => void;
}) {
  const { mutateAsync, isLoading } = useMutation(AdminDeletePostDocument, {});
  const { openToast } = useToast();
  const logError = useLogError();

  return {
    adminDeletePost: async () => {
      try {
        const response = await mutateAsync({ postId });
        if (response.data.adminDeletePost.__typename !== 'MutationAdminDeletePostSuccess') {
          logError({
            action: POSTING_ERROR_ACTIONS.DELETE_POST_FAILED,
            error: new Error("Couldn't delete post"),
            errorType: ERROR_TYPE.POSTING,
            isArtistOnlyPage: false,
            level: 'error',
            message: "Couldn't delete post",
            pillar: 'POSTING',
            toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
            indexedTags: {
              postId,
            },
          });
        } else {
          onDeleteSuccess();
          openToast({ text: 'The post was removed.', variant: 'success' });
        }
      } catch (error) {
        logError({
          action: POSTING_ERROR_ACTIONS.DELETE_POST_FAILED,
          error,
          errorType: ERROR_TYPE.POSTING,
          isArtistOnlyPage: false,
          level: 'error',
          message: "Couldn't delete post",
          pillar: 'POSTING',
          toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
          indexedTags: {
            postId,
          },
        });
      }
    },
    loading: isLoading,
  };
}
